import type { FlightEvent } from "@ca/db/schema";
import type { RiskLevel } from "@ca/types/risk";

import {
	SINK_RATE_BAND_CEILING_FT, SINK_RATE_BAND_FLOOR_FT, SINK_RATE_FLIGHT_EVENT_TYPE, type SinkRateBand,
} from "@ca/db/schema/flight-events";

export type SinkRateRun = {
	aglFt: null | number;
	band: SinkRateBand;
	endUnix: number;
	peakUnix: number;
	startUnix: number;
};

const SINK_RATE_BAND_RISK_LEVELS: Record<SinkRateBand, RiskLevel> = {
	a: "moderate",
	b: "elevated",
	no_elevation: "elevated",
};

const RISK_LEVEL_RANK: Record<RiskLevel, number> = {
	elevated: 2,
	low: 0,
	moderate: 1,
};

export const unixify = (timestamp: string | Date) => {
	return Math.round(new Date(timestamp).getTime() / 1000);
};

export const sinkRateBandToRiskLevel = (band: SinkRateBand): RiskLevel => SINK_RATE_BAND_RISK_LEVELS[band];

export const toSinkRateRuns = (events: FlightEvent[]): SinkRateRun[] => {
	const runs: SinkRateRun[] = [];
	for (const event of events) {
		if (event.type !== SINK_RATE_FLIGHT_EVENT_TYPE) {
			continue;
		}
		const peakUnix = unixify(event.timestamp);
		// Rows written before banding carry no data; they render at the severe treatment.
		const data = event.data?.type === SINK_RATE_FLIGHT_EVENT_TYPE ? event.data : null;
		runs.push({
			aglFt: data?.aglFt ?? null,
			band: data?.band ?? "b",
			endUnix: data ? unixify(data.runEnd) : peakUnix,
			peakUnix,
			startUnix: data ? unixify(data.runStart) : peakUnix,
		});
	}
	return runs;
};

export type BandEdgeCrossing = {
	index: number;
	thresholdFt: number;
};

export const bandEdgeCrossings = (aglSeries: (null | number)[]): BandEdgeCrossing[] => {
	const crossings: BandEdgeCrossing[] = [];
	for (const thresholdFt of [SINK_RATE_BAND_CEILING_FT, SINK_RATE_BAND_FLOOR_FT]) {
		let index: null | number = null;
		for (let i = 1; i < aglSeries.length; i += 1) {
			const previous = aglSeries[i - 1];
			const current = aglSeries[i];
			if (previous == null || current == null || previous < thresholdFt || current >= thresholdFt) {
				continue;
			}
			index = (i - 1) + ((previous - thresholdFt) / (previous - current));
		}
		if (index !== null) {
			crossings.push({
				index,
				thresholdFt,
			});
		}
	}
	return crossings;
};

export const sinkRateRunAt = (runs: SinkRateRun[], unix: number): null | SinkRateRun => {
	return runs.find((run) => unix >= run.startUnix && unix <= run.endUnix) ?? null;
};

export const mostSevereSinkRateRiskLevel = (events: FlightEvent[]): null | RiskLevel => {
	let mostSevere: null | RiskLevel = null;
	for (const run of toSinkRateRuns(events)) {
		const level = sinkRateBandToRiskLevel(run.band);
		if (mostSevere === null || RISK_LEVEL_RANK[level] > RISK_LEVEL_RANK[mostSevere]) {
			mostSevere = level;
		}
	}
	return mostSevere;
};
